'use es6';

import { useEffect, useMemo } from 'react';
import invariant from 'react-utils/invariant';
import Immutable from 'immutable';
import http from 'hub-http/clients/apiClient';
import useControllable from 'UIComponents/hooks/useControllable';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import AppInstallability from '../records/AppInstallability';
import { getAppUninstallableReason } from '../utils/installability';
export const useAppInstallability = ({
  installability,
  offeringId,
  apiBase = 'ecosystem/v1',
  onFetchAppInstallabilityError
}) => {
  const [appInstallability, setInstallability] = useControllable(installability, installability, () => {});
  invariant(installability || offeringId, 'useAppInstallability hook expects either an AppInstallability instance or an offeringId');
  invariant(!installability || installability instanceof AppInstallability, 'installability must be an AppInstallability record');
  useEffect(() => {
    if (!installability) {
      http.get(`${apiBase}/apps/${offeringId}/installability`).then(resp => setInstallability(new SyntheticEvent(new AppInstallability(resp)))).catch(err => {
        if (typeof onFetchAppInstallabilityError === 'function') {
          onFetchAppInstallabilityError(err);
        } else {
          throw err;
        }
      });
    }
  }, [installability, offeringId, apiBase, onFetchAppInstallabilityError, setInstallability]);
  const result = useMemo(() => {
    if (!appInstallability) {
      return {};
    }
    const {
      incompatibleHubInstalls,
      incompatibleHubUserInstalls,
      missingHubUserRoles,
      missingHubScopes,
      missingHubScopeGroups
    } = appInstallability;
    const appUninstallableReason = getAppUninstallableReason(appInstallability);
    let isAppInstallable = false;
    let appInstallabilityExtras = Immutable.Set();
    if (missingHubUserRoles.size && !missingHubScopes.size) {
      appInstallabilityExtras = missingHubUserRoles;
    } else if (missingHubScopes.size || missingHubScopeGroups.size) {
      const missingScopes = missingHubScopes.size ? missingHubScopes : missingHubScopeGroups;
      appInstallabilityExtras = missingScopes;
    } else if (incompatibleHubInstalls.size || incompatibleHubUserInstalls.size) {
      const incompatibleInstalls = incompatibleHubInstalls.size ? incompatibleHubInstalls : incompatibleHubUserInstalls;
      appInstallabilityExtras = incompatibleInstalls;
    } else {
      isAppInstallable = true;
    }
    return {
      isAppInstallable,
      appUninstallableReason,
      appInstallabilityExtras
    };
  }, [appInstallability]);
  return result;
};